export type MicrophonePermissionState =
  | "checking"
  | "granted"
  | "prompt"
  | "denied"
  | "unavailable";

export async function queryMicrophonePermission(): Promise<MicrophonePermissionState> {
  if (!navigator.mediaDevices?.getUserMedia) return "unavailable";
  if (!navigator.permissions?.query) return "prompt";
  try {
    const status = await navigator.permissions.query({ name: "microphone" as PermissionName });
    if (status.state === "granted" || status.state === "denied") return status.state;
    return "prompt";
  } catch {
    // Safari does not consistently expose microphone through Permissions API.
    // A user gesture can still make the authoritative getUserMedia request.
    return "prompt";
  }
}

export async function requestMicrophoneStream(): Promise<MediaStream> {
  if (!navigator.mediaDevices?.getUserMedia) throw new Error("microphone_unavailable");
  return navigator.mediaDevices.getUserMedia({ audio: true });
}

export function classifyMicrophoneFailure(error: unknown): "denied" | "unavailable" {
  if (
    error instanceof DOMException
    && ["NotAllowedError", "SecurityError", "PermissionDeniedError"].includes(error.name)
  ) return "denied";
  return "unavailable";
}

export function microphoneRecoveryCopy(
  userAgent = typeof navigator === "undefined" ? "" : navigator.userAgent,
  hostname = typeof location === "undefined" ? "this site" : location.hostname,
): string {
  if (/iPad|iPhone|iPod/i.test(userAgent)) {
    return "Open iPhone Settings, find Mark (or Safari), enable Microphone, then return and try again.";
  }
  if (/Android/i.test(userAgent)) {
    return `Open regular Chrome (not Mark), visit ${hostname}, then use ⋮ → Settings → Site settings → Microphone → ${hostname} → Allow. Return here and try again.`;
  }
  return "Open this site's browser permissions, allow Microphone, then return and try again.";
}
